import type { MultipleOpportunity, SingleOpportunity } from '@ps/web-sports-api-types';
import classNames from 'clsx';
import React from 'react';
import { useField } from 'react-final-form';

import type { OpportunityInput } from '@swf/core/models/sports/betSlip/types';
import { getIsAvailableOpportunity } from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import { Icon } from '@swf/sports/components';

import { calculateTotalPotentialReturns } from '../../../../../../utils/calculateTotalPotentialReturns';
import PromotionControl from '../PromotionControl/PromotionControl';

import styles from './Stake.scss';
import StakeInput from './components/StakeInput/StakeInput';

interface Props {
  className?: string;
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const Stake: React.FC<Props> = ({ className, opportunity }) => {
  const { input } = useField<OpportunityInput | undefined>(opportunity.id, {
    subscription: { value: true }
  });

  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);
  const winPotentialReturn = isAvailableOpportunity
    ? calculateTotalPotentialReturns({
        stakePerLine: input.value?.stakePerLineParsed || 0,
        placeReturnFactor: opportunity.placeReturnFactor,
        returnFactor: opportunity.returnFactor,
        numberOfLines: opportunity.numberOfLines,
        eachWaySelected: input.value?.eachWaySelected,
        isFreeBet: Boolean(input.value?.freeBet?.length)
      })
    : 0;
  const actualNumberOfLines = isAvailableOpportunity
    ? input.value?.eachWaySelected
      ? opportunity.numberOfLines * 2
      : opportunity.numberOfLines
    : 0;
  const isWinPotentialReturnAboveMaxPayout =
    isAvailableOpportunity && opportunity.maxPayout
      ? winPotentialReturn / 100 > opportunity.maxPayout
      : false;
  const numberOfLines = isAvailableOpportunity
    ? actualNumberOfLines || opportunity.numberOfLines
    : 0;

  return (
    <div className={className}>
      <div
        className={classNames(styles.inputWrapper, {
          [styles.inputDisabled]: !isAvailableOpportunity
        })}
      >
        {!isAvailableOpportunity && <Icon name="padlock" className={styles.suspended} size="xl" />}

        <StakeInput
          fieldName={opportunity.id}
          minStake={isAvailableOpportunity ? opportunity.minStake : 0}
          maxStake={
            isAvailableOpportunity
              ? input.value?.eachWaySelected
                ? opportunity.eachWayMax
                : opportunity.maxStake
              : 0
          }
          minLineStake={isAvailableOpportunity ? opportunity.minLineStake : 0}
          minStakeIncrement={isAvailableOpportunity ? opportunity.minStakeIncrement : 0}
          maxPayout={isAvailableOpportunity ? opportunity.maxPayout : 0}
          isWinPotentialReturnAboveMaxPayout={isWinPotentialReturnAboveMaxPayout}
          numberOfLines={numberOfLines}
          opportunity={opportunity}
        />

        <PromotionControl
          fieldName={opportunity.id}
          numberOfLines={numberOfLines}
          opportunity={opportunity}
        />
      </div>
    </div>
  );
};

export default Stake;
